import React, { useEffect, useState, useRef, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Row, Col, Button, Spinner,Form, Modal, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import FormInput from "../FormInput";
import { logout } from "../../redux/actions/AuthActions";
import { toast } from "react-toastify";
import swal from 'sweetalert2';
import Preloader from "../Preloader";
import '../../styles/common.css';
import {  getIndustries, getSectors, profileDetails, updateSupplier } from "../../redux/actions/SupplierActions";
import Select from "react-dropdown-select";
import ReactSelect from "react-select";
import { fetchStates, fetchCities } from "../../redux/actions/MasterActions";

const UpdateProfile = ({ show, onHide, editId }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({});
  const [keyPoints, setKeyPoints] = useState([]);
  const [keyPointInput, setKeyPointInput] = useState("");
  const [supplierType, setSupplierType] = useState([
    { id: "Service", name: "Service" },
    { id: "Manufacturing", name: "Manufacturing" }
]);
  const dispatch = useDispatch();
  const lastSectorIdRef = useRef(null);
  const objectUrlsRef = useRef([]);

  const {
    AuthReducer: { auth, errors },
    supplierReducer: { supplierDetails, errorsSupplier, loadingSupplier,changedSupplierStatus,sectors,industries,message },
    masterReducer: { cmYuvaStateList, cmYuvaCityList }
  } = useSelector((state) => state);

  useEffect(() => {
    setFormData({});
    lastSectorIdRef.current = null;
    dispatch(profileDetails(auth?.token));
    dispatch(getSectors());
    dispatch(fetchStates());
  }, [auth]);

  useEffect(() => {
    if (editId && supplierDetails) {
      const initialKeyPoints = JSON.parse(supplierDetails?.key_points || "[]"); 
      setKeyPoints(Array.isArray(initialKeyPoints) ? initialKeyPoints : []);
      setFormData({
        name: supplierDetails.name,
        designation: supplierDetails.designation,
        additional_info: supplierDetails.additional_info,
        address: supplierDetails.address,
        brochure: supplierDetails.brochure,
        business_name: supplierDetails.business_name,
        city: supplierDetails.city,
        city_id: supplierDetails.city_id || '',
        email: supplierDetails.email,
        enterprise_logo: supplierDetails.enterprise_logo,
        gst_number: supplierDetails.gst_number,
        key_points: Array.isArray(initialKeyPoints) ? initialKeyPoints : [],
        mobile: supplierDetails.mobile,
        photo_graph: supplierDetails.photo_graph,
        pincode: supplierDetails.pincode,
        profile_image: supplierDetails.profile_image,
        remark: supplierDetails.remark,
        sectors: [supplierDetails.sectors[0]?.sector?.id],
        industries: supplierDetails.industries.map(item => item?.industry?.id),
        signature: supplierDetails.signature,
        state: supplierDetails.state,
        state_id: supplierDetails.state_id || '',
        total_experience: supplierDetails.total_experience,
        type: supplierDetails.type,
        website_url: supplierDetails.website_url,
        bank_name: supplierDetails.bank_name,
        bank_ifsc_code: supplierDetails.bank_ifsc_code,
        bank_branch_name: supplierDetails.bank_branch_name,
        bank_account_number: supplierDetails.bank_account_number,
        bank_person_name: supplierDetails.bank_person_name,
        aadhar: supplierDetails.aadhar || '',
        udyam_registration_number: supplierDetails.udyam_registration_number || '',
    });
    
    // Load cities if state_id exists
    if (supplierDetails.state_id) {
      dispatch(fetchCities(supplierDetails.state_id));
    }
    }
  }, [supplierDetails, editId]);

  useEffect(() => {
    const currentSectorId = formData.sectors && formData.sectors.length > 0 ? formData.sectors[0] : null;
    
    if (currentSectorId && currentSectorId !== lastSectorIdRef.current) {
      lastSectorIdRef.current = currentSectorId;
      dispatch(getIndustries(formData.sectors));
    }
  }, [formData.sectors, dispatch]);

  // Match state name to state_id if state_id is missing but state name exists
  useEffect(() => {
    if (formData?.state && !formData?.state_id && cmYuvaStateList.length > 0) {
      const matchedState = cmYuvaStateList.find(state => 
        state.label.toLowerCase() === formData.state.toLowerCase()
      );
      if (matchedState) {
        setFormData(prev => ({ ...prev, state_id: matchedState.value }));
        dispatch(fetchCities(matchedState.value));
      }
    }
  }, [cmYuvaStateList, formData?.state, formData?.state_id]);

  // Match city name to city_id if city_id is missing but city name exists
  useEffect(() => {
    if (formData?.city && !formData?.city_id && formData?.state_id && cmYuvaCityList.length > 0) {
      const matchedCity = cmYuvaCityList.find(city => 
        city.label.toLowerCase() === formData.city.toLowerCase()
      );
      if (matchedCity) {
        setFormData(prev => ({ ...prev, city_id: matchedCity.value }));
      }
    }
  }, [cmYuvaCityList, formData?.city, formData?.city_id, formData?.state_id]);

  const inputHandler = (e) => {
    const { value, name, files, type } = e.target;
    if (files && files.length > 0) {
      // Validate file types for image uploads
      if (name === "profile_image" || name === "enterprise_logo" || name === "signature") {
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(files[0].type)) {
          toast.error(t("Please upload only image files (JPEG, PNG, GIF, WEBP)"));
          return;
        }
      }
      setFormData({ ...formData, [name]: files[0] });
    } else {
      // For total_experience, only allow positive numbers
      if (name === "total_experience" && value !== "" && type === "number") {
        const numValue = parseFloat(value);
        if (isNaN(numValue) || numValue < 1) {
          return; // Don't update if invalid
        }
      }
      // For Aadhar, only allow numeric input (12 digits)
      if (name === "aadhar") {
        const numericValue = value.replace(/\D/g, ''); // Remove non-numeric characters
        if (numericValue.length > 12) {
          return; // Don't allow more than 12 digits
        }
        setFormData({ ...formData, [name]: numericValue });
        return;
      }
      // For Udyam Registration Number, only allow alphanumeric (but typically numeric)
      if (name === "udyam_registration_number") {
        const alphanumericValue = value.replace(/[^A-Za-z0-9]/g, ''); // Remove special characters
        setFormData({ ...formData, [name]: alphanumericValue });
        return;
      }
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleStateChange = (selectedOption) => {
    if (selectedOption) {
      setFormData(prev => ({ 
        ...prev, 
        state_id: selectedOption.value,
        state: selectedOption.label,
        city: '',
        city_id: ''
      }));
      dispatch(fetchCities(selectedOption.value));
    } else {
      setFormData(prev => ({ 
        ...prev, 
        state_id: '',
        state: '',
        city: '',
        city_id: ''
      }));
    }
  };

  const handleCityChange = (selectedOption) => {
    if (selectedOption) {
      setFormData(prev => ({ 
        ...prev, 
        city_id: selectedOption.value,
        city: selectedOption.label
      }));
    } else {
      setFormData(prev => ({ 
        ...prev, 
        city_id: '',
        city: ''
      }));
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const form = event.currentTarget;
  
    // Validate sectors
    if (!formData.sectors || !Array.isArray(formData.sectors) || formData.sectors.length === 0) {
      toast.error(t("Please select a sector"));
      return;
    }

    // Validate industries
    if (!formData.industries || !Array.isArray(formData.industries) || formData.industries.length === 0) {
      toast.error(t("Please select at least one industry"));
      return;
    }
  
    if (form.checkValidity() === false) {
      event.stopPropagation();
      return;
    }
    const formDataToSend = new FormData();
    for (let key in formData) {
      // Skip file fields that are strings (existing file paths) - only send new files
      if (key === 'profile_image' || key === 'enterprise_logo' || key === 'signature' || key === 'brochure') {
        if (formData[key] && formData[key] instanceof File) {
          formDataToSend.append(key, formData[key]);
        }
        continue;
      }
      
      if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
        if (Array.isArray(formData[key])) {
          formData[key].forEach((item) => {
            formDataToSend.append(`${key}[]`, item); 
          });
        } else {
          formDataToSend.append(key, formData[key]);
        }
      }
    }
    // Explicitly add state_id and city_id
    if (formData.state_id) {
      formDataToSend.append('state_id', formData.state_id);
    }
    if (formData.city_id) {
      formDataToSend.append('city_id', formData.city_id);
    }
    // Explicitly add remark and additional_info to ensure they are sent
    if (formData.remark) {
      formDataToSend.append('remark', formData.remark);
    }
    if (formData.additional_info) {
      formDataToSend.append('additional_info', formData.additional_info);
    }
    if (formData.website_url) {
      formDataToSend.append('website_url', formData.website_url);
    }
    if (formData.gst_number) {
      formDataToSend.append('gst_number', formData.gst_number);
    }
    if (formData.aadhar) {
      formDataToSend.append('aadhar', formData.aadhar);
    }
    if (formData.udyam_registration_number) {
      formDataToSend.append('udyam_registration_number', formData.udyam_registration_number);
    }
  
    dispatch(updateSupplier(auth?.token, formDataToSend));
  };


  useEffect(() => {
    if (errors && errors?.status === 401) {
      dispatch(logout());
      toast.error(errors?.data?.message);
      onHide();
    }
  }, [errors, dispatch, onHide]);

  const handleAddKeyPoint = () => {
    if (keyPointInput.trim() !== "") {
      setKeyPoints((prev) => {
        const updatedKeyPoints = [...prev, keyPointInput];
        setFormData((prevFormData) => ({
          ...prevFormData,
          key_points: updatedKeyPoints,
        }));
    
        return updatedKeyPoints;
      });
      setKeyPointInput("");
    }
  };
  const handleDeleteKeyPoint = (index) => {
    setKeyPoints((prev) => {
      const updatedKeyPoints = prev.filter((_, i) => i !== index);
      setFormData((prevFormData) => ({
        ...prevFormData,
        key_points: updatedKeyPoints,
      }));
      return updatedKeyPoints;
    });
  };

  useEffect(()=>{
  },[formData]);

  // Create object URLs for file previews
  const profileImageUrl = useMemo(() => {
    if (formData?.profile_image && formData.profile_image instanceof File) {
      return URL.createObjectURL(formData.profile_image);
    }
    return null;
  }, [formData?.profile_image]);

  const brochureUrl = useMemo(() => {
    if (formData?.brochure && formData.brochure instanceof File) {
      return URL.createObjectURL(formData.brochure);
    }
    return null;
  }, [formData?.brochure]);

  const enterpriseLogoUrl = useMemo(() => {
    if (formData?.enterprise_logo && formData.enterprise_logo instanceof File) {
      return URL.createObjectURL(formData.enterprise_logo);
    }
    return null;
  }, [formData?.enterprise_logo]);

  const signatureUrl = useMemo(() => {
    if (formData?.signature && formData.signature instanceof File) {
      return URL.createObjectURL(formData.signature);
    }
    return null;
  }, [formData?.signature]);

  // Store URLs for cleanup
  useEffect(() => {
    const urls = [profileImageUrl, brochureUrl, enterpriseLogoUrl, signatureUrl].filter(Boolean);
    objectUrlsRef.current = urls;
    
    return () => {
      urls.forEach(url => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, [profileImageUrl, brochureUrl, enterpriseLogoUrl, signatureUrl]);


  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          {t("Update Supplier")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {errorsSupplier?.data?.message && (
          <div className="alert alert-danger">
            {errorsSupplier?.data?.message}
          </div>
        )}
        <Row>
          <Col lg={6}>
            <FormInput
              label={t("Name")}
              type="text"
              errors={errorsSupplier?.data}
              value={formData?.name}
              name="name"
              onChange={inputHandler}
              containerClass="mb-3"
              required
            />
          </Col>
          <Col lg={6}>
            <FormInput
              label={t("Email")}
              type="text"
              errors={errorsSupplier?.data}
              value={formData?.email}
              name="email"
              onChange={inputHandler}
              containerClass="mb-3"
              required
            />
          </Col>
          <Col lg={6}>
            <FormInput
              label={t("Mobile")}
              type="text"
              errors={errorsSupplier?.data}
              value={formData?.mobile}
              name="mobile"
              onChange={inputHandler}
              containerClass="mb-3"
              required
            />
          </Col>
          <Col lg={6}>
            <FormInput
              label={t("Designation")}
              type="text"
              errors={errorsSupplier?.designation}
              value={formData.designation}
              name="designation"
              onChange={inputHandler}
              containerClass="mb-3"
              required
            />
          </Col>
          <Col lg={6}>
            <FormInput
              label={t("Total Experience")}
              type="number"
              min="1"
              errors={errorsSupplier?.data}
              value={formData?.total_experience}
              name="total_experience"
              onChange={inputHandler}
              containerClass="mb-3"
              required
            />
          </Col>
          <Col lg={6}>
            <FormInput
              label={t("Bussiness Name")}
              type="text"
              errors={errorsSupplier?.data}
              value={formData?.business_name}
              name="business_name"
              onChange={inputHandler}
              containerClass="mb-3"
              required
            />
          </Col>
          <Col lg={6}>
            <Form.Group className="mb-3">
              <Form.Label>{t("Sector")} <span className="text-danger">*</span></Form.Label>
                <Select
                    options={sectors}
                    values={sectors.filter(option => option.id == formData?.sectors) || []} // Fixed comparison
                    name="sectors"
                    labelField="name"
                    valueField="id"
                    searchBy="name"
                    onChange={(selectedData) => {
                        if (selectedData.length > 0) {
                            setFormData(prev => ({
                                ...prev,
                                sectors: [selectedData[0]?.id]
                            }));
                        } else {
                            setFormData(prev => ({
                                ...prev,
                                sectors: []
                            }));
                        }
                    }}
                    styles={null}
                />
                {errorsSupplier?.data?.sectors && (
                  <div className="invalid-feedback d-block">
                      <p className="text-danger">{errorsSupplier?.data?.sectors}</p>
                  </div>
                )}
            </Form.Group>
          </Col>
          <Col lg={6}>
             <Form.Group className="mb-3">
              <Form.Label>{t("Industries")} <span className="text-danger">*</span></Form.Label>
                <Select
                  options={industries}
                  values={industries.filter(option => formData?.industries?.includes(option.id)) || []} 
                  name="industries"
                  labelField="name"
                  valueField="id"
                  searchBy="name"
                  multi={true}
                  onChange={(selectedData) => {
                      setFormData(prev => ({
                          ...prev,
                          industries: selectedData.map(item => item.id)
                      }));
                  }}
                  styles={null}
                />
                {errorsSupplier?.data?.industries && (
                  <div className="invalid-feedback d-block">
                      <p className="text-danger">{errorsSupplier?.data?.industries}</p>
                  </div>
                )}
            </Form.Group>
          </Col>
          <Col lg={6}>
            <Form.Group className="mb-3">
              <Form.Label>{t("Supplier Type")}</Form.Label>
                <Select
                options={supplierType}
                values={formData?.type ? supplierType.filter((option) => option.id === formData?.type) : []}
                name="type"
                labelField={"name"}
                valueField={"id"}
                searchBy={"name"}
                onChange={(selectedData) => {
                  setFormData(prev => ({
                      ...prev,
                      type: selectedData.length > 0 ? selectedData[0]?.id : null
                  }));
                }}
                styles={null}
                />
                {errorsSupplier?.data?.type && (
                  <div className="invalid-feedback d-block">
                      <p className="text-danger">{errorsSupplier?.data?.type}</p>
                  </div>
                )}
            </Form.Group>
          </Col>
          <Col lg={6}>
            <Form.Group className="mb-3">
              <Form.Label>{t("State")}</Form.Label>
              <ReactSelect
                value={formData?.state_id ? { value: formData.state_id, label: formData.state } : null}
                onChange={handleStateChange}
                options={cmYuvaStateList.map(state => ({
                  value: state.value,
                  label: state.label
                }))}
                placeholder="Select State"
                isSearchable
                classNamePrefix="select"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderColor: errorsSupplier?.data?.state ? '#dc3545' : base.borderColor,
                  }),
                }}
              />
              {errorsSupplier?.data?.state && (
                <div className="invalid-feedback d-block">
                  <p className="text-danger">{errorsSupplier?.data?.state}</p>
                </div>
              )}
            </Form.Group>
          </Col>
          <Col lg={6}>
            <Form.Group className="mb-3">
              <Form.Label>{t("City")}</Form.Label>
              <ReactSelect
                isDisabled={!formData?.state_id}
                value={formData?.city_id ? { value: formData.city_id, label: formData.city } : null}
                onChange={handleCityChange}
                options={cmYuvaCityList.map(city => ({
                  value: city.value,
                  label: city.label
                }))}
                placeholder="Select City"
                isSearchable
                classNamePrefix="select"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderColor: errorsSupplier?.data?.city ? '#dc3545' : base.borderColor,
                  }),
                }}
              />
              {errorsSupplier?.data?.city && (
                <div className="invalid-feedback d-block">
                  <p className="text-danger">{errorsSupplier?.data?.city}</p>
                </div>
              )}
            </Form.Group>
          </Col>
          <Col lg={6}>
            <FormInput
              label={t("Pincode")}
              type="text"
              errors={errorsSupplier?.data}
              value={formData?.pincode}
              name="pincode"
              onChange={inputHandler}
              containerClass="mb-3"
              maxLength={6}
              required
            />
          </Col>
          <Col lg={12}>
            <FormInput
              label={t("Address")}
              type="textarea"
              errors={errorsSupplier?.data}
              value={formData?.address || ''}
              name="address"
              onChange={inputHandler}
              containerClass="mb-3"
              required
            />
          </Col>
          <Col lg={6}>
            <FormInput
              label={t("GST Number")}
              type="text"
              errors={errorsSupplier?.data}
              value={formData?.gst_number || ''}
              name="gst_number"
              onChange={inputHandler}
              containerClass="mb-3"
              required
            />
          </Col>
          <Col lg={6}>
            <FormInput
              label={t("Website URL")}
              type="text"
              errors={errorsSupplier?.data}
              value={formData?.website_url || ''}
              name="website_url"
              onChange={inputHandler}
              containerClass="mb-3"
              required
            />
          </Col>
          <Col lg={6}>
            <FormInput
              label={t("Aadhar Number")}
              type="text"
              errors={errorsSupplier?.data}
              value={formData?.aadhar || ''}
              name="aadhar"
              onChange={inputHandler}
              containerClass="mb-3"
              maxLength={12}
              placeholder="Enter 12 digit Aadhar number"
            />
          </Col>
          <Col lg={6}>
            <FormInput
              label={t("Udyam Registration Number")}
              type="text"
              errors={errorsSupplier?.data}
              value={formData?.udyam_registration_number || ''}
              name="udyam_registration_number"
              onChange={inputHandler}
              containerClass="mb-3"
              placeholder="Enter Udyam Registration Number"
            />
          </Col>
          <Col lg={6}>
            <FormInput
              label={t("Profile Image")}
              type="file"
              errors={errorsSupplier?.data}
              name="profile_image"
              onChange={inputHandler}
              containerClass="mb-3"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
            />
            {formData?.profile_image && (
              <div className="mt-2">
                {typeof formData.profile_image === 'string' ? (
                  <>
                    <img
                      src={`https://msmesupplier.s3.ap-south-1.amazonaws.com/${formData.profile_image}`}
                      alt="Profile"
                      className="img-thumbnail"
                      style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover' }}
                    />
                    <small className="text-muted d-block mt-1">Current: {formData.profile_image}</small>
                  </>
                ) : profileImageUrl ? (
                  <>
                    <img
                      src={profileImageUrl}
                      alt="Profile Preview"
                      className="img-thumbnail"
                      style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover' }}
                    />
                    <small className="text-muted d-block mt-1">New: {formData.profile_image.name}</small>
                  </>
                ) : null}
              </div>
            )}
          </Col>
          <Col lg={6}>
            <FormInput
              label={t("Brochure")}
              type="file"
              errors={errorsSupplier?.data}
              name="brochure"
              onChange={inputHandler}
              containerClass="mb-3"
              accept="application/pdf,image/jpeg,image/jpg,image/png"
            />
            {formData?.brochure && (
              <div className="mt-2">
                {typeof formData.brochure === 'string' ? (
                  <>
                    {formData.brochure.toLowerCase().endsWith('.pdf') ? (
                      <a
                        href={`https://msmesupplier.s3.ap-south-1.amazonaws.com/${formData.brochure}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-primary"
                      >
                        View Current Brochure (PDF)
                      </a>
                    ) : (
                      <img
                        src={`https://msmesupplier.s3.ap-south-1.amazonaws.com/${formData.brochure}`}
                        alt="Brochure"
                        className="img-thumbnail"
                        style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover' }}
                      />
                    )}
                    <small className="text-muted d-block mt-1">Current: {formData.brochure}</small>
                  </>
                ) : brochureUrl ? (
                  <>
                    {formData.brochure.type === 'application/pdf' ? (
                      <a
                        href={brochureUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-primary"
                      >
                        View New Brochure (PDF)
                      </a>
                    ) : (
                      <img
                        src={brochureUrl}
                        alt="Brochure Preview"
                        className="img-thumbnail"
                        style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover' }}
                      />
                    )}
                    <small className="text-muted d-block mt-1">New: {formData.brochure.name}</small>
                  </>
                ) : null}
              </div>
            )}
          </Col>
          <Col lg={6}>
            <FormInput
              label={t("Enterprise Logo")}
              type="file"
              errors={errorsSupplier?.data}
              name="enterprise_logo"
              onChange={inputHandler}
              containerClass="mb-3"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
            />
            {formData?.enterprise_logo && (
              <div className="mt-2">
                {typeof formData.enterprise_logo === 'string' ? (
                  <>
                    <img
                      src={`https://msmesupplier.s3.ap-south-1.amazonaws.com/${formData.enterprise_logo}`}
                      alt="Enterprise Logo"
                      className="img-thumbnail"
                      style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover' }}
                    />
                    <small className="text-muted d-block mt-1">Current: {formData.enterprise_logo}</small>
                  </>
                ) : enterpriseLogoUrl ? (
                  <>
                    <img
                      src={enterpriseLogoUrl}
                      alt="Enterprise Logo Preview"
                      className="img-thumbnail"
                      style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover' }}
                    />
                    <small className="text-muted d-block mt-1">New: {formData.enterprise_logo.name}</small>
                  </>
                ) : null}
              </div>
            )}
          </Col>
          <Col lg={12}>
            <FormInput
              label={t("Remark")}
              type="textarea"
              errors={errorsSupplier?.data}
              value={formData?.remark || ''}
              name="remark"
              onChange={inputHandler}
              containerClass="mb-3"
              required
            />
          </Col>
          <Col lg={12}>
            <div className="d-flex align-items-center gap-2">
              <FormInput
                label={t("Add Key Points")}
                type="text"
                errors={errorsSupplier?.data}
                value={keyPointInput}
                name="name"
                onChange={(e) => setKeyPointInput(e.target.value)}
                containerClass="mb-0"
                required
              />
              <Button type="button" className="mt-3" onClick={handleAddKeyPoint} disabled={!keyPointInput.trim()}>
                {t("Add")}
              </Button>
            </div>
          </Col>
          {keyPoints.length > 0 && (
            <Col lg={12} className="mt-3">
              <Table bordered>
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Key Points</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {keyPoints.map((keyPoint, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{keyPoint}</td>
                      <td>
                        <Button variant="danger" size="sm" onClick={() => handleDeleteKeyPoint(index)}>
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Col>
          )}
          <Col lg={6}>
            <FormInput
              label={t("Signature")}
              type="file"
              errors={errorsSupplier?.data}
              name="signature"
              onChange={inputHandler}
              containerClass="mb-3"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
            />
            {formData?.signature && (
              <div className="mt-2">
                {typeof formData.signature === 'string' ? (
                  <>
                    <img
                      src={`https://msmesupplier.s3.ap-south-1.amazonaws.com/${formData.signature}`}
                      alt="Signature"
                      className="img-thumbnail"
                      style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover' }}
                    />
                    <small className="text-muted d-block mt-1">Current: {formData.signature}</small>
                  </>
                ) : signatureUrl ? (
                  <>
                    <img
                      src={signatureUrl}
                      alt="Signature Preview"
                      className="img-thumbnail"
                      style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover' }}
                    />
                    <small className="text-muted d-block mt-1">New: {formData.signature.name}</small>
                  </>
                ) : null}
              </div>
            )}
          </Col>
          <Col lg={12}>
            <FormInput
              label={t("Additional Information")}
              type="textarea"
              errors={errorsSupplier?.data}
              value={formData?.additional_info || ''}
              name="additional_info"
              onChange={inputHandler}
              containerClass="mb-3"
              required
            />
          </Col>
          <Col lg={6}>
            <FormInput
              label={t("Bank Name")}
              type="text"
              errors={errorsSupplier?.data}
              value={formData?.bank_name}
              name="bank_name"
              onChange={inputHandler}
              containerClass="mb-3"
              required
            />
          </Col>
          <Col lg={6}>
            <FormInput
              label={t("Bank Ifsc Code")}
              type="text"
              errors={errorsSupplier?.data}
              value={formData?.bank_ifsc_code}
              name="bank_ifsc_code"
              onChange={inputHandler}
              containerClass="mb-3"
              required
            />
          </Col>
          <Col lg={6}>
            <FormInput
              label={t("Bank Branch Name")}
              type="text"
              errors={errorsSupplier?.data}
              value={formData?.bank_branch_name}
              name="bank_branch_name"
              onChange={inputHandler}
              containerClass="mb-3"
              required
            />
          </Col>
          <Col lg={6}>
            <FormInput
              label={t("Bank Account Number")}
              type="number"
              min="0"
              errors={errorsSupplier?.data}
              value={formData?.bank_account_number}
              name="bank_account_number"
              onChange={inputHandler}
              containerClass="mb-3"
              required
            />
          </Col>
          <Col lg={6}>
            <FormInput
              label={t("Bank Person Name")}
              type="text"
              errors={errorsSupplier?.data}
              value={formData?.bank_person_name}
              name="bank_person_name"
              onChange={inputHandler}
              containerClass="mb-3"
              required
            />
          </Col>
         
         
        </Row>
        <div className="d-flex justify-content-center">
          {loadingSupplier ? (
            <Button color="primary" disabled>
              <Spinner animation="border" size="sm" />
              {t("Please wait ...")}
            </Button>
          ) : (
            <Button type="button" onClick={handleSubmit} disabled={loadingSupplier}>
              {t("Update")}
            </Button>
          )}
        </div>
      </Modal.Body>
      {loadingSupplier && <Preloader />}
    </Modal>
  );
};

export default UpdateProfile;
